import { CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled, LoadingOutlined } from "@ant-design/icons";
import { Button, Flex, Progress, Typography } from "antd";
import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRootPath } from "../../hooks/app.hook";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { getClientDetailsByUUID } from "../../service/client.service";
import { getNotesDetails } from "../../service/notes.service";
import { setLoadingNotesState, setNotesViewVisible, setSelectedClient } from "../../slices/appStateSlice";
import { setNotesDetails, setRecordingId } from "../../slices/notesSlice";
import type { EHRTransferStatus, TransferJob } from "../../types/index.type";
import { useTransfer } from "../TransferButton/TransferContext";

const { Text } = Typography;

// Coarse mapping of statuses → percent. Not exact, but conveys forward
// motion; matches the wireframe's compact fixed-width progress bar.
const STATUS_PROGRESS: Record<EHRTransferStatus, number> = {
  not_started: 0,
  pending: 10,
  logging_in: 25,
  awaiting_mfa: 30,
  patient_lookup: 45,
  awaiting_appointment: 55,
  awaiting_note_type: 60,
  template_mapping: 75,
  writing_note: 90,
  completed: 100,
  failed: 100,
};

const SLOW_THRESHOLD_MS = 300_000;
const COMPLETED_DISMISS_MS = 4_000;

const isAwaitingAction = (status: EHRTransferStatus) =>
  status === "awaiting_appointment" || status === "awaiting_note_type" || status === "awaiting_mfa";

const TransferBanner: React.FC = () => {
  const { activeJob } = useTransfer();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { rootPath } = useRootPath();
  const { notesDetails } = useAppSelector((state) => state.notes);

  // Tick once a second so the "30s+" copy and elapsed time stay fresh
  // without forcing the parent context to re-render every tick.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!activeJob) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [activeJob]);

  // Auto-dismiss the green/red flash for completed/failed jobs.
  const [dismissed, setDismissed] = useState<string | null>(null);
  useEffect(() => {
    if (!activeJob) return;
    if (activeJob.status === "completed" || activeJob.status === "failed") {
      const t = setTimeout(() => setDismissed(activeJob.id), COMPLETED_DISMISS_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [activeJob]);

  if (!activeJob) return null;
  if (dismissed === activeJob.id) return null;

  const status = activeJob.status;
  const isFailed = status === "failed";
  const isCompleted = status === "completed";
  const awaitingAction = isAwaitingAction(status);

  const elapsedMs = activeJob.created_at ? Date.now() - new Date(activeJob.created_at).getTime() : 0;
  const isSlow = elapsedMs >= SLOW_THRESHOLD_MS && !awaitingAction && !isCompleted && !isFailed;

  // The "Go to note" CTA only shows when the user is somewhere else.
  const isOnNote = activeJob.audio_file_id != null && notesDetails?.id === activeJob.audio_file_id;

  const handleGoToNote = async () => {
    if (!activeJob.audio_file_id || !activeJob.client_uuid) return;
    try {
      const [noteRes, clientRes] = await Promise.all([
        getNotesDetails(activeJob.audio_file_id),
        getClientDetailsByUUID(activeJob.client_uuid),
      ]);
      dispatch(setLoadingNotesState(true));
      dispatch(setSelectedClient(clientRes.data));
      dispatch(setNotesDetails(noteRes.data));
      dispatch(setRecordingId(activeJob.audio_file_id));
      dispatch(setNotesViewVisible(true));
      navigate(`${rootPath}/clients/${activeJob.client_uuid}`);
    } catch (err) {
      console.error("Failed to deep-link to transfer note:", err);
    }
  };

  return (
    <div style={containerStyle(status)}>
      <Flex align="center" gap={12} style={{ width: "100%" }}>
        <Icon status={status} />
        <Flex vertical style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ color: textColor(status) }}>
            {primaryCopy(activeJob, awaitingAction, isFailed, isCompleted, isSlow)}
          </Text>
          <Text style={{ color: textColor(status), opacity: 0.85, fontSize: 12 }}>{secondaryCopy(activeJob)}</Text>
        </Flex>
        <Progress
          percent={STATUS_PROGRESS[status] ?? 0}
          showInfo={false}
          status={isFailed ? "exception" : isCompleted ? "success" : "active"}
          strokeColor={isFailed ? "#ff4d4f" : isCompleted ? "#52c41a" : awaitingAction ? "#faad14" : "#1677ff"}
          style={{ width: 180, marginBottom: 0 }}
        />
        {!isOnNote && !isFailed && !isCompleted && activeJob.audio_file_id != null && (
          <Button size="small" onClick={handleGoToNote} type={awaitingAction ? "primary" : "default"}>
            Go to note
          </Button>
        )}
      </Flex>
    </div>
  );
};

const Icon: React.FC<{ status: EHRTransferStatus }> = ({ status }) => {
  if (status === "completed") return <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} />;
  if (status === "failed") return <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 18 }} />;
  if (isAwaitingAction(status)) return <ExclamationCircleFilled style={{ color: "#faad14", fontSize: 18 }} />;
  return <LoadingOutlined style={{ color: "#1677ff", fontSize: 18 }} spin />;
};

const containerStyle = (status: EHRTransferStatus): React.CSSProperties => {
  const base: React.CSSProperties = {
    padding: "10px 16px",
    borderBottom: "1px solid",
    display: "flex",
    alignItems: "center",
  };
  if (status === "completed") return { ...base, background: "#f6ffed", borderColor: "#b7eb8f" };
  if (status === "failed") return { ...base, background: "#fff1f0", borderColor: "#ffa39e" };
  if (isAwaitingAction(status)) return { ...base, background: "#fffbe6", borderColor: "#ffe58f" };
  return { ...base, background: "#e6f4ff", borderColor: "#91caff" };
};

const textColor = (status: EHRTransferStatus): string => {
  if (status === "completed") return "#237804";
  if (status === "failed") return "#a8071a";
  if (isAwaitingAction(status)) return "#874d00";
  return "#003a8c";
};

const primaryCopy = (
  job: TransferJob,
  awaitingAction: boolean,
  isFailed: boolean,
  isCompleted: boolean,
  isSlow: boolean,
): string => {
  const noteLabel = job.patient_name ? `${job.patient_name}'s note` : "your note";
  const ehr = job.ehr_name || "your EHR";

  if (isCompleted) return `${noteLabel} was transferred to ${ehr}`;
  if (isFailed) return `Couldn't transfer ${noteLabel} to ${ehr}`;
  if (awaitingAction) {
    if (job.status === "awaiting_mfa") return `Action needed — enter the verification code`;
    if (job.status === "awaiting_note_type") return `Action needed — choose a note template`;
    return `Action needed — pick the appointment for ${job.patient_name}`;
  }
  if (isSlow) return `Transferring ${noteLabel} to ${ehr} — this may take a bit longer`;
  return `Transferring ${noteLabel} to ${ehr}`;
};

const secondaryCopy = (job: TransferJob): string => {
  const parts: string[] = [];
  if (job.patient_name) parts.push(job.patient_name);
  if (job.ehr_name) parts.push(job.ehr_name);
  return parts.join(" · ");
};

export default TransferBanner;
